import { AuthIntent } from './authIntent';
import { isPrivatePath } from '../navigation/privatePaths';

/** Where a brand-new account is sent: the one page that can activate it. */
export const ACTIVATION_PATH = '/get-started';

/** sessionStorage key the auth flow stashes its destination under. */
export const POST_AUTH_REDIRECT_KEY = 'postAuthRedirect';

/**
 * Where to land someone once they are authenticated.
 *
 * Previously this was unconditionally the path they were on when the modal opened,
 * which meant a visitor who registered from the header on the homepage was returned
 * to the homepage — signed in, on a marketing page, with nothing telling them what
 * to do next. /get-started was already built for exactly that moment: for an
 * authenticated visitor it creates a project and an API key inline, shows per-step
 * state, and is the only place `activation_complete` fires. Nothing navigated there
 * after registration, so both the activation and the metric depended on the visitor
 * finding an unlinked page on their own.
 *
 * The rule:
 *
 *   - Registering (`signUp`) goes to /get-started, because there is nothing else a
 *     new account usefully does first.
 *   - Signing in returns to where they were, which is what a returning customer
 *     expects.
 *   - Either way, an explicit request for a signed-in-only page wins. Someone who
 *     followed a link to /billing and authenticated to reach it asked for /billing,
 *     and sending them to onboarding instead would lose the thing they came for.
 *
 * Note this is intent-based, not state-based. A returning customer who happens to
 * click Create Account lands on /get-started, which shows their steps already
 * complete — harmless. Detecting "is this account actually new" would need the
 * project and key lookups that /get-started itself performs, so doing it here would
 * duplicate them on every sign-in to no benefit.
 */
export function postAuthDestination(intent: AuthIntent, currentPath: string): string {
  if (isPrivatePath(currentPath)) return currentPath;
  return intent === 'signUp' ? ACTIVATION_PATH : currentPath;
}
